import { useCallback, useState } from "react";
import { useMediaQuery } from "./useMediaQuery";

export type SidebarMode = "expanded" | "rail" | "drawer";

const COLLAPSED_PREF_KEY = "sidebar:collapsed";

function readCollapsedPref(): boolean | null {
  const stored = localStorage.getItem(COLLAPSED_PREF_KEY);
  if (stored === null) return null;
  return stored === "true";
}

/**
 * Sidebar state machine:
 * - mobile (<768px): overlay drawer, opened by hamburger
 * - desktop: expanded (w-64) or icon rail; auto-rails below lg (1024px)
 *   unless the user has manually toggled — the manual preference wins
 *   at any width and persists in localStorage.
 */
export function useSidebar() {
  const isMobile = !useMediaQuery("(min-width: 768px)");
  const isLgUp = useMediaQuery("(min-width: 1024px)");

  const [collapsedPref, setCollapsedPref] = useState<boolean | null>(
    readCollapsedPref,
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const collapsed = collapsedPref ?? !isLgUp;
  const mode: SidebarMode = isMobile
    ? "drawer"
    : collapsed
    ? "rail"
    : "expanded";

  const toggleCollapsed = useCallback(() => {
    setCollapsedPref((prev) => {
      const next = !(prev ?? !isLgUp);
      localStorage.setItem(COLLAPSED_PREF_KEY, String(next));
      return next;
    });
  }, [isLgUp]);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return {
    mode,
    isMobile,
    mobileOpen,
    toggleCollapsed,
    openMobile,
    closeMobile,
  };
}
